import { useState } from "react"
import { FaCircleCheck, FaFile } from "react-icons/fa6"
import { FiInfo } from "react-icons/fi"
import { useNavigate } from "react-router-dom"
import { Box, Center, HStack, Tooltip, VStack } from "@chakra-ui/react"
import { Button, Text, Toaster, toaster, UploadModal } from "@/components"
import { useViewer } from "@/contexts"
import { useClientDeals, useUploadDocuments } from "@/hooks"

const InfoTooltip = ({ children }: { children: React.ReactNode }) => (
  <Tooltip.Root openDelay={0} closeDelay={100}>
    <Tooltip.Trigger asChild>
      <Box as="span" cursor="pointer" display="inline-flex" alignItems="center" ml={1}>
        <FiInfo size={14} color="#718096" />
      </Box>
    </Tooltip.Trigger>
    <Tooltip.Positioner>
      <Tooltip.Content bg="gray.700" color="white" px={3} py={2} borderRadius="md" maxW="350px">
        <Tooltip.Arrow>
          <Tooltip.ArrowTip />
        </Tooltip.Arrow>
        {children}
      </Tooltip.Content>
    </Tooltip.Positioner>
  </Tooltip.Root>
)

type BcOnboardingStep = "check-email" | "upload-id" | "upload-equity" | "thank-you"

const DOC_TYPE_TO_PREFIX: Record<string, string> = {
  "ID - Passport": "ID - Passport",
  "ID - Driver's License": "ID - Drivers License",
  "Stock Option Plan": "SOP",
  "Stock Option or RSU Grant": "SOGA",
}

const WHITE_LISTED_FILE_TYPES = [
  "image/png",
  "image/jpeg",
  "image/jpg",
  "application/pdf",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/msword"
]

const STEP_ORDER: BcOnboardingStep[] = ["check-email", "upload-id", "upload-equity", "thank-you"]

const nextStep = (current: BcOnboardingStep): BcOnboardingStep => {
  const idx = STEP_ORDER.indexOf(current)
  return STEP_ORDER[idx + 1] ?? "thank-you"
}

export const BcOnboardingComponent = () => {
  const navigate = useNavigate()
  const { viewedClient } = useViewer()
  const { data: deals } = useClientDeals()
  const { mutate: uploadDocuments, isPending, isSuccess, reset } = useUploadDocuments()

  const [onboardingStep, setOnboardingStep] = useState<BcOnboardingStep>("check-email")
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [currentDocType, setCurrentDocType] = useState<string>("")
  const [hasUploadedInStep, setHasUploadedInStep] = useState(false)
  const [uploadedFiles, setUploadedFiles] = useState<Record<string, string[]>>({})

  const goToNextStep = () => {
    setHasUploadedInStep(false)
    setOnboardingStep(nextStep(onboardingStep))
  }

  const openUploadModal = (docType: string) => {
    reset()
    setCurrentDocType(docType)
    setIsModalOpen(true)
  }

  const handleConfirmUpload = (filesToUpload: File[]) => {
    const deal = deals[0]
    if (!deal || !currentDocType) return

    for (const file of filesToUpload) {
      if (!WHITE_LISTED_FILE_TYPES.includes(file.type)) {
        toaster.create({
          title: `${file.type} is not a valid file type`,
          type: "error",
          closable: true,
          duration: 3000,
        })
        return
      }
    }

    const documentType = DOC_TYPE_TO_PREFIX[currentDocType]
    uploadDocuments(
      { documentType, dealIdStr: deal.id.toString(), files: filesToUpload },
      {
        onSuccess: () => {
          toaster.create({
            title: "Files uploaded successfully",
            type: "success",
            duration: 5000,
          })
          setUploadedFiles(prev => ({
            ...prev,
            [currentDocType]: [...(prev[currentDocType] ?? []), ...filesToUpload.map(f => f.name)]
          }))
          setIsModalOpen(false)
          setHasUploadedInStep(true)
        },
        onError: () => {
          toaster.create({
            title: "Upload failed. Please contact your equity partner.",
            type: "error",
            duration: 3000,
          })
        },
      }
    )
  }

  // Step 1: Check email for DocuSign
  if (onboardingStep === "check-email") {
    return (
      <Center w="100%" h="100%">
        <VStack position="relative" zIndex={1} w={{ base: "92%", md: "50%" }} p={{ base: 5, md: 8 }} borderRadius={10} bg="white" boxShadow="md" mt={20} gap={4}>
          <Text fontSize="xs" color="gray.400" alignSelf="flex-end">Step 1/3</Text>
          <Box borderRadius={5} bg="blue.300" p={3}>
            <FaCircleCheck size={30} color="white" />
          </Box>
          <Text fontSize="lg" fontWeight="bold" textAlign="center">
            Check Your Email
          </Text>
          <Text fontSize="sm" textAlign="center">
            Please check your email for a DocuSign Background Check Permission Form sent from{" "}
            <Text as="span" fontWeight="bold">docusign@esofund.com</Text>. There will be a link in the email that will take you to the DocuSign form. Please review and sign the form to give us permission to run a background check, which is required to proceed with your deal.
            Be sure to check your spam/junk folder if you don't see it in your inbox.
          </Text>
          <HStack w="100%" justifyContent="center">
            <Button variant="solid" onClick={() => setOnboardingStep("upload-id")}>
              Continue
            </Button>
          </HStack>
        </VStack>
      </Center>
    )
  }

  // Step 2: Upload 2 Forms of ID (Passport + License)
  if (onboardingStep === "upload-id") {
    return (
      <Center w="100%" h="100%">
        <VStack position="relative" zIndex={1} w={{ base: "92%", md: "50%" }} p={{ base: 5, md: 8 }} borderRadius={10} bg="white" boxShadow="md" mt={20} gap={4} align="stretch">
          <Text fontSize="xs" color="gray.400" alignSelf="flex-end">Step 2/3</Text>
          <Text fontSize="lg" fontWeight="bold">
            Upload 2 Forms of ID!
          </Text>
          <Text fontSize="sm" fontWeight="semibold">What we need from you:</Text>
          <Text fontSize="sm" fontWeight="bold">ID - Passport</Text>
          <Box pl={4}>
            <Text fontSize="sm">- A clear photo or scan of your passport ID page</Text>
            <Text fontSize="sm">- If you have a Green Card or Visa, please also attach a copy.</Text>
          </Box>
          <HStack justifyContent="flex-end">
            <Button variant="outline" size="sm" onClick={() => openUploadModal("ID - Passport")}>Upload Passport</Button>
          </HStack>
          {uploadedFiles["ID - Passport"]?.length > 0 && (
            <Box bg="green.50" border="1px solid" borderColor="green.200" borderRadius="md" px={4} py={2}>
              {uploadedFiles["ID - Passport"].map((name, i) => (
                <HStack key={i} gap={2}>
                  <FaFile size={10} color="#38A169" />
                  <Text fontSize="xs" color="green.700">{name}</Text>
                </HStack>
              ))}
            </Box>
          )}
          <Text fontSize="sm" fontWeight="bold">ID - Driver's License</Text>
          <Box pl={4}>
            <Text fontSize="sm">- A clear photo of the front of your driver's license</Text>
            <Text fontSize="sm">- If the address on your driver's license does not match the address you submitted on the Background Check Permission Form, please also submit a utility bill or other proof of your current address.</Text>
          </Box>
          <HStack justifyContent="flex-end">
            <Button variant="outline" size="sm" onClick={() => openUploadModal("ID - Driver's License")}>Upload License</Button>
          </HStack>
          {uploadedFiles["ID - Driver's License"]?.length > 0 && (
            <Box bg="green.50" border="1px solid" borderColor="green.200" borderRadius="md" px={4} py={2}>
              {uploadedFiles["ID - Driver's License"].map((name, i) => (
                <HStack key={i} gap={2}>
                  <FaFile size={10} color="#38A169" />
                  <Text fontSize="xs" color="green.700">{name}</Text>
                </HStack>
              ))}
            </Box>
          )}
          <HStack justifyContent="flex-end" gap={3} pt={2}>
            <Button variant={hasUploadedInStep ? "solid" : "outline"} onClick={goToNextStep}>
              {hasUploadedInStep ? "Next" : "I'll Do This Later"}
            </Button>
          </HStack>
        </VStack>
        <UploadModal
          isOpen={isModalOpen}
          onOpenChange={setIsModalOpen}
          title={`Upload ${currentDocType}`}
          uploadDocuments={handleConfirmUpload}
          isLoading={isPending}
          isSuccess={isSuccess}
        />
        <Toaster />
      </Center>
    )
  }

  // Step 3: Upload Equity Docs (SOP + SOGA)
  if (onboardingStep === "upload-equity") {
    return (
      <Center w="100%" h="100%">
        <VStack position="relative" zIndex={1} w={{ base: "92%", md: "50%" }} p={{ base: 5, md: 8 }} borderRadius={10} bg="white" boxShadow="md" mt={20} gap={4} align="stretch">
          <Text fontSize="xs" color="gray.400" alignSelf="flex-end">Step 3/3</Text>
          <Text fontSize="lg" fontWeight="bold">
            One Last Thing: Your Equity Docs
          </Text>
          <Text fontSize="sm" fontWeight="semibold">What we need from you:</Text>
          <HStack gap={0}>
            <Text fontSize="sm" fontWeight="bold">Stock Option Plan</Text>
            <InfoTooltip>
              <Text fontSize="xs">The master plan document governing your stock options, issued by your employer. Usually found in your equity portal. If unavailable, request it from your stock plan administrator or HR.</Text>
            </InfoTooltip>
          </HStack>
          <Box pl={4}>
            <Text fontSize="sm">- The plan document from your employer, usually in your equity portal.</Text>
          </Box>
          <HStack justifyContent="flex-end">
            <Button variant="outline" size="sm" onClick={() => openUploadModal("Stock Option Plan")}>Upload SOP</Button>
          </HStack>
          {uploadedFiles["Stock Option Plan"]?.length > 0 && (
            <Box bg="green.50" border="1px solid" borderColor="green.200" borderRadius="md" px={4} py={2}>
              {uploadedFiles["Stock Option Plan"].map((name, i) => (
                <HStack key={i} gap={2}>
                  <FaFile size={10} color="#38A169" />
                  <Text fontSize="xs" color="green.700">{name}</Text>
                </HStack>
              ))}
            </Box>
          )}
          <HStack gap={0}>
            <Text fontSize="sm" fontWeight="bold">Stock Option or RSU Grant</Text>
            <InfoTooltip>
              <Text fontSize="xs">Your grant agreement showing the number of options, grant date, vesting schedule, and strike price. Typically found in your equity portal (Carta, Shareworks, etc.). If unavailable, contact your stock plan administrator.</Text>
            </InfoTooltip>
          </HStack>
          <Box pl={4}>
            <Text fontSize="sm">- The grant document from your employer, usually in your equity portal.</Text>
          </Box>
          <HStack justifyContent="flex-end">
            <Button variant="outline" size="sm" onClick={() => openUploadModal("Stock Option or RSU Grant")}>Upload Grant</Button>
          </HStack>
          {uploadedFiles["Stock Option or RSU Grant"]?.length > 0 && (
            <Box bg="green.50" border="1px solid" borderColor="green.200" borderRadius="md" px={4} py={2}>
              {uploadedFiles["Stock Option or RSU Grant"].map((name, i) => (
                <HStack key={i} gap={2}>
                  <FaFile size={10} color="#38A169" />
                  <Text fontSize="xs" color="green.700">{name}</Text>
                </HStack>
              ))}
            </Box>
          )}
          <HStack justifyContent="flex-end" gap={3} pt={2}>
            <Button variant={hasUploadedInStep ? "solid" : "outline"} onClick={goToNextStep}>
              {hasUploadedInStep ? "Next" : "I'll Do This Later"}
            </Button>
          </HStack>
        </VStack>
        <UploadModal
          isOpen={isModalOpen}
          onOpenChange={setIsModalOpen}
          title={`Upload ${currentDocType}`}
          uploadDocuments={handleConfirmUpload}
          isLoading={isPending}
          isSuccess={isSuccess}
        />
        <Toaster />
      </Center>
    )
  }

  // Thank you step
  return (
    <Center w="100%" h="100%">
      <VStack position="relative" zIndex={1} w={{ base: "92%", md: "50%" }} p={{ base: 5, md: 8 }} borderRadius={10} bg="white" boxShadow="md" mt={20} gap={4}>
        <Box borderRadius={5} bg="green.300" p={3}>
          <FaCircleCheck size={30} color="white" />
        </Box>
        <Text fontSize="lg" fontWeight="bold" textAlign="center">
          Thank You!
        </Text>
        <Text fontSize="sm" textAlign="center">
          Don't forget to check your email for a DocuSign link sent from docusign@esofund.com which you and your Equity Partner will sign. In the meantime, you can go to the documents page to upload any files you still need to upload.
          If you have any questions, feel free to email or schedule a call with your equity partner!
        </Text>
        <Button variant="solid" onClick={() => navigate(viewedClient ? `/clients/${viewedClient.id}` : "/documents")}>
          Proceed to Documents
        </Button>
      </VStack>
    </Center>
  )
}
